import { hasAnyPermission, useAuthorization } from '@frontend/authorization';
import { EntityType, Rights } from '@frontend/common';
import { CommonMessage } from '@frontend/lang';
import { useActiveModal, useModals } from '@frontend/modal';
import { useAccountRepository } from '@frontend/repository';
import { SlotActuatorClient } from '@frontend/slot/api';
import { SlotExtended } from '@frontend/slot/types';
import { ToastUtil } from '@frontend/toast-utils';
import { useNavigate } from 'react-router-dom';

import { useAppDispatch } from '../../redux/store';

const useSlotDetailModal = () => {
    const { currentAccount } = useAccountRepository();
    const dispatch = useAppDispatch();
    const navigate = useNavigate();
    const { closeAllModals, closeModal, openModal } = useModals();
    const { modalId } = useActiveModal();
    const { permissions } = useAuthorization();
    const openSlotModalId = 'slot-detail-modal-open-slot';
    const showIdentificationModalId = 'slot-identification-view';

    const canReadIdentifications =
        permissions != null &&
        currentAccount != null &&
        hasAnyPermission(permissions, [{ item: EntityType.IDENTIFICATION, source: EntityType.ACCOUNT, right: Rights.READ, uid: currentAccount }]);

    async function viewDetailedConstraintsList(slot_id: string) {
        await navigate(`/constraints/constraints?slot_id=${slot_id}`);
        closeModal(modalId);
    }

    async function viewDetailedLendingTransactionsList(slot_id: string) {
        await navigate(`/lending/transactions?slot_id=${slot_id}`);
        closeModal(modalId);
    }

    const onOpenSlotConfirmed = async (slot: SlotExtended) => {
        const openSlot = SlotActuatorClient.openSlot(slot.account_id, slot.spot_id, slot.module_id, slot.id, {});
        return ToastUtil.promise(
            () => openSlot,
            { message: CommonMessage.OBJECTS.SLOT.PENDING_OPENING_MESSAGE(EntityType.SLOT, slot.display) },
            {
                message: CommonMessage.OBJECTS.SLOT.SUCCESS_OPENING_MESSAGE(EntityType.SLOT, slot.display),
                onVoidSuccess: () => closeModal(openSlotModalId)
            },
            { message: CommonMessage.OBJECTS.SLOT.ERROR_OPENING_MESSAGE(EntityType.SLOT, slot.display) }
        );
    };

    return {
        currentAccount,
        dispatch,
        navigate,
        closeAllModals,
        closeModal,
        openModal,
        openSlotModalId,
        showIdentificationModalId,
        onOpenSlotConfirmed,
        viewDetailedConstraintsList,
        viewDetailedLendingTransactionsList,
        canReadIdentifications
    };
};

export default useSlotDetailModal;
